/**
 * 🚙 FLEET MANAGER SERVICE - Integration with fleet manager monitoring APIs
 * Handles fleet manager compliance monitoring, criteria configuration, and manual interventions
 */

// API Base URL
import { getApiBaseUrl } from '@/config/environment';


// Types for Fleet Manager API
export interface FleetManager {
  id: string;
  name: string;
  email: string;
  phone?: string;
  hostType: string;
  applicationDate: string;
  verificationDeadline: string;
  daysRemaining: number | null;
  vehiclesOnboarded: number;
  vehiclesRequired: number;
  complianceStatus: 'compliant' | 'at_risk' | 'non_compliant';
  downgradeScheduled?: boolean;
  downgradeDate?: string;
  lastReminderSent?: string;
}

export interface FleetManagerSummary {
  total: number;
  compliant: number;
  atRisk: number;
  nonCompliant: number;
}

export interface FleetCriteria {
  minVehicles: number;
  verificationPeriodDays: number;
  gracePeriodDays?: number;
  autoDowngradeEnabled?: boolean;
  lastUpdated?: string;
  updatedBy?: string;
}

export interface FleetMonitorResponse {
  success: boolean;
  data: {
    fleetManagers: FleetManager[];
    summary: FleetManagerSummary;
    criteria: {
      minVehicles: number;
      verificationPeriodDays: number;
    };
  };
  error?: string;
}

export interface FleetCriteriaResponse {
  success: boolean;
  data?: FleetCriteria;
  message?: string;
  error?: string;
}

export interface FleetActionResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
}

export const fleetManagerService = {

  /**
   * 📊 Get all fleet managers with compliance status
   */
  async getFleetManagers(filters?: {
    status?: 'all' | 'compliant' | 'at_risk' | 'non_compliant';
  }): Promise<FleetMonitorResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }

      const url = `${getApiBaseUrl()}/api/v1/admin/fleet/monitor${queryParams.toString() ? `?${queryParams}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Fleet Monitor API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching fleet managers:', error);

      return {
        success: false,
        data: {
          fleetManagers: [],
          summary: {
            total: 0,
            compliant: 0,
            atRisk: 0,
            nonCompliant: 0
          },
          criteria: {
            minVehicles: 5,
            verificationPeriodDays: 7
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch fleet managers'
      };
    }
  },

  /**
   * ⚙️ Get fleet criteria configuration
   */
  async getFleetCriteria(): Promise<FleetCriteriaResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/fleet/criteria`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Criteria API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error fetching fleet criteria:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch criteria'
      };
    }
  },

  /**
   * 🔄 Update fleet criteria configuration
   */
  async updateFleetCriteria(criteria: {
    minVehicles?: number;
    verificationPeriodDays?: number;
    gracePeriodDays?: number;
    autoDowngradeEnabled?: boolean;
    applyToExisting?: boolean;
    reason?: string;
  }): Promise<FleetCriteriaResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/fleet/criteria`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(criteria)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Update Criteria API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error updating fleet criteria:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update criteria'
      };
    }
  },

  /**
   * ⏰ Grant extension to a fleet manager
   */
  async grantExtension(hostId: string, extensionDays: number, reason: string): Promise<FleetActionResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/fleet/grant-extension`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hostId, extensionDays, reason })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Grant Extension API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error granting extension:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to grant extension'
      };
    }
  },

  /**
   * ⬇️ Manually downgrade a fleet manager
   */
  async downgradeFleetManager(hostId: string, reason: string): Promise<FleetActionResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/fleet/downgrade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hostId, reason })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Downgrade API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error downgrading fleet manager:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to downgrade fleet manager'
      };
    }
  },

  /**
   * ✅ Check compliance for all fleet managers
   */
  async checkCompliance(): Promise<FleetActionResponse> {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/fleet/check-compliance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Check Compliance API Error ${response.status}:`, errorText);
        throw new Error(`API Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('❌ Error checking compliance:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check compliance'
      };
    }
  }
};
